import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  NgZone,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor, NgModel, FormGroup } from '@angular/forms';
import { MapsAPILoader } from '@agm/core';
import { Observable } from 'rxjs';
import { Utils } from './utils.service';
import { FormValidationService } from './form-validation.service';
// TODO: ld-tooltip will be replaced by ula-tooltip; this is a breaking change
import { TooltipContentComponent } from '../tooltip';
import { maskedNumberInputShiftWorkaround } from '../../utils/masked-number-input-shift-workaround';

const noop = () => {};


export const GENERIC_INPUT_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => UlaInputGenericComponent),
  multi: true
};


let nextUniqueId = 0;

// More reading: https://github.com/angular/material2/blob/master/src/lib/input/input.ts
// More reading: http://almerosteyn.com/2016/04/linkup-custom-control-to-ngcontrol-ngmodel
@Component({
  selector: 'ula-input-generic, ula-textarea',
  templateUrl: './ula-input-generic.template.html',
  styleUrls: ['./ula-input.style.css'],
  providers: [GENERIC_INPUT_CONTROL_VALUE_ACCESSOR,
    FormValidationService],
  encapsulation: ViewEncapsulation.None
})
export class UlaInputGenericComponent implements ControlValueAccessor, OnInit {
  public counter: number = nextUniqueId++;
  public _valid: boolean = true;
  public _readonly: boolean = false;

  @ViewChild('input') input: ElementRef;
  @ViewChild('inputModel') inputModel: NgModel;

  public _focused: boolean = false;
  public _value: any = '';
  public _elementType: 'input' | 'textarea';

  private _onTouchedCallback: () => void = noop;
  private _onChangeCallback: (_: any) => void = noop;

  @Input() validationForm: FormGroup;
  @Input() displayName: string;
  @Input() formControlName: string;
  @Input() shouldCheckIsDirty: boolean = false;
  @Input() hideError: boolean = false;

  /**
   * Misc
   */
  @Input() title: string = '';
  @Input() labelPrefix: string;
  @Input() label: string;
  // TODO: ld-tooltip will be replaced by ula-tooltip; this is a breaking change
  @Input() popover: string | TooltipContentComponent;
  @Input() popoverPlacement: 'top' | 'bottom' | 'left' | 'right' = 'right';
  @Input() titleIconBefore: string = null;
  @Input() titleIconAfter: string = null;

  /**
   * Bindings.
   */
  // TODO: if the maskingType is 'currency', either the max should be supplied,
  // which would then programatically generate the max length,
  // or vice versa
  @Input() max: string | number = null;
  // only really used for maskingType "phoneExt", because PL and Mortgage
  // currently have different requirements. Plus, the "x" shouldn't count to the maxlength
  @Input() maxDigits: number = null;
  @Input() step: number = 0.1;
  @Input() maxlength: number = null;
  @Input() min: string | number = null;
  @Input() minlength: number = null;
  @Input() placeholder: string = null;
  // this value maps to the HTML 'type' attribute (e.g. "text" or "number")
  @Input() type: string = 'text';
  // this value represents any extra logic used to format user input
  // (e.g. adding dashes after the 3rd and 5th digits for a Social Security Number)
  @Input() maskingType:
  null | 'ssn' | 'phone' | 'zipCode' | 'currency' | 'phoneExt' | 'digitsOnly' = null;
  @Input() name: string = null;
  @Input() qa: string;
  @Input() role: string;
  @Input() mask: string = null;
  // This form is only used by autocompleteAddress type inputs.
  @Input() addressComponents: {} = null;

  private _disabled: boolean = false;
  private _required: boolean = false;
  private _spellcheck: boolean = false;

  @Input()
  get disabled(): boolean { return this._disabled; }
  set disabled(value) { this._disabled = this.coerceBooleanProperty(value); }

  @Input()
  get required(): boolean { return this._required; }
  set required(value) { this._required = this.coerceBooleanProperty(value); }

  @Input()
  get spellcheck(): boolean { return this._spellcheck; }
  set spellcheck(value) { this._spellcheck = this.coerceBooleanProperty(value); }

  @Input()
  get readonly(): boolean { return this._readonly; }
  set readonly(value) { this._readonly = this.coerceBooleanProperty(value); }

  private _blurEmitter: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();
  private _focusEmitter: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();

  @Output('blur')
  get onBlur(): Observable<FocusEvent> { return this._blurEmitter.asObservable(); }

  @Output('focus')
  get onFocus(): Observable<FocusEvent> { return this._focusEmitter.asObservable(); }

  @Output() ngModelChange: EventEmitter<any> = new EventEmitter(false);

  get value(): any { return this._value; };

  @Input() set value(v: any) {
    v = this._convertValueForInputType(v);

    // TODO: this should be updated to work with the currency FilterType.
    // but also how should we deal with min values?
    // this will be easier if we ever move error messages inside this component
    if (!!this.max) {
      if (this.type === 'number' && (v > this.max)) {
        v = this._value;
        this.input.nativeElement.value = this._value;
      } else if (this.maskingType === 'currency') {

        const justDigitsAndDecimal = v.replace(/[^\d.]/g, '');
        const valueAsFloat = Number.parseFloat(justDigitsAndDecimal);

        if (valueAsFloat > this.max) {
          v = this._formatCurrencyString(this.max);
          this.input.nativeElement.value = v;
        }
      }
    }

    if (this.maxlength && (v.toString().length > this.maxlength)) {
      v = this._value;
      this.input.nativeElement.value = this._value;
    }

    if (this.maskingType === 'currency') {
      const vString = v.toString();

      // if currency contains more than one decimal point
      if (vString.replace(/[^.]/g, '').length > 1) {
        v = this._value;
        this.input.nativeElement.value = this._value;
      }

      const decimalPosition = vString.indexOf('.');
      if (decimalPosition >= 0) {
        const afterDecimal = vString.substring(decimalPosition + 1);
        let maxLengthAfterDecimal;

        switch (this.step) {
          case .01:
            maxLengthAfterDecimal = 2;
            break;
          case .0001:
            maxLengthAfterDecimal = 4;
            break;
        }

        if (afterDecimal.length > maxLengthAfterDecimal) {

          // if input holds is "12.10", this._value will actually be "12.1"
          // since that will obviously feel like a bug, use weird hack around
          // to ensure the trailing 0's are retained.
          this._value = this._value.substr(0, decimalPosition) + '.' +
            afterDecimal.substr(0, maxLengthAfterDecimal);

          v = this._value;
          this.input.nativeElement.value = this._value;
        }
      }
    }

    if (this.maskingType === 'phoneExt') {
      const vString = v.toString();
      let lastPosition = vString.lastIndexOf('x');
      let firstPosition = vString.indexOf('x');
      let stringLength = vString.length;
      if (lastPosition > firstPosition || firstPosition > 0) {
        v = this._value;
        this.input.nativeElement.value = this._value;
      }

      const digitsOnly = vString.replace(/[^\d]/, '');

      if (!!this.maxDigits && digitsOnly.length > this.maxDigits) {
        v = this._value;
        this.input.nativeElement.value = this._value;
      }
    }

    if (v !== this._value) {

      switch (this.maskingType) {
        case 'ssn':
          v = Utils.formatSSN(v);
          break;
        case 'phone':
          v = Utils.formatPhoneNumber(v);
          break;
        case 'zipCode':
          v = this.utils.formatZipCode(v);
          break;
        case 'currency':
          v = this._formatCurrencyString(v);
          break;
        case null:
          break;
      }

      this._value = v;
      this._onChangeCallback(v);
    }
  }

  _formatCurrencyString(v: any): string {
    let numDecimalPlaces: 0 | 2 | 4 = 2;

    switch (this.step) {
      case 1:
        numDecimalPlaces = 0;
        break;
      default:
      case .01:
        numDecimalPlaces = 2;
        break;
      case .0001:
        numDecimalPlaces = 4;
        break;
    }

    return Utils.numberToCurrencyString(v, numDecimalPlaces);
  }

  // because of auto formatting (commas, dashes, '$'),
  // if a masking type is specified, the inputType should be 'text'
  getInputType(): string {
    let ret: string;

    switch (this.maskingType) {
      case null:
        ret = this.type;
        break;
      // due to characters added during masking, the below types should override the "this.type" property to text
      case 'currency':
      case 'zipCode':
      case 'ssn':
      case 'phone':
      case 'phoneExt':
        ret = 'text';
        break;
    }

    const validHtml5Types = ['button', 'checkbox', 'color', 'date', 'datetime-local', 'email', 'file', 'hidden',
      'image', 'month', 'number', 'password', 'radio', 'range', 'reset', 'search', 'submit',
      'tel', 'text', 'time', 'url', 'week'];
    if (validHtml5Types.indexOf(ret) === -1) {
      return 'text'; // IE11 freaks out instead of falling back to text with non-standard input types
    }

    return ret;
  }

  // return a default step value of 0.01 for currency inputs if no explicit step value
  getStepValue(): number {
    let ret = this.step;

    if (this.maskingType === 'currency' && !this.step) {
      ret = 0.01;
    }

    return ret;
  }

  // some masking types will, by their definition, have a predefined max length
  // this function defines these values
  getMaxLength(): string | number {
    let ret: string | number;

    if (this.maskingType === 'phone') {
      // TODO: What about extensions?
      ret = this.formValidationService.maxlength.phone;
    } else if (this.maskingType === 'ssn') {
      ret = this.formValidationService.maxlength.ssn;
    } else if (this.maskingType === 'zipCode') {
      ret = this.formValidationService.maxlength.zipCode;
    } else if (this.maskingType === 'phoneExt') {
      ret = this.formValidationService.maxlength.phoneExt;
      this.maxlength = ret;
    } else {
      // TODO: this should be maxlength. Don't want to change that and break a page relying on this faulty logic
      ret = this.max;
    }

    return ret;
  }

  constructor(elementRef: ElementRef,
    private utils: Utils,
    private formValidationService: FormValidationService,
    private mapsAPILoader: MapsAPILoader,
    private ngZone: NgZone) {
    this._elementType = elementRef.nativeElement.nodeName.toLowerCase() === 'ula-textarea' ? 'textarea' : 'input';
  }

  ngOnInit(): void {
    if (this.type === 'autocompleteAddress') {

      // TODO test the validity of the api key before trying to load.
      // If the key is invalid, load will cause an error that is unhandleable.
      // It loads a script which validates the key, and does not use the error channel of the Promise.
      this.mapsAPILoader.load().then(() => {

        const options = {
          types: ['address'],
          componentRestrictions: { country: 'us' }
        };

        let autocomplete = new google.maps.places.Autocomplete(this.input.nativeElement, options);

        autocomplete.addListener('place_changed', (e) => {
          this.ngZone.run(() => {
            let place: google.maps.places.PlaceResult = autocomplete.getPlace();

            // If address components are omitted, the input field is set to the result of the autocomplete.
            if (!place.address_components || !this.addressComponents) {
              return;
            }

            const componentValues = place.address_components.reduce((prev, current) => {
              const addressComponentType = this.addressComponents[current.types[0]];

              // we're using the short name in all cases (e.g. WA instead of Washington)
              if (!!addressComponentType && prev[addressComponentType]) {
                prev[addressComponentType] += ' ' + current.short_name;
              } else if (!!addressComponentType) {
                prev[addressComponentType] = current.short_name;
              }

              return prev;
            }, {});

            Object.keys(componentValues).forEach(key => {
              if (key === 'address') {
                const addressNumberMatch = this.input.nativeElement.value.match(/(^\d+)\s+/);
                const returnedAddressNumberMatch = componentValues[key].match(/(^\d+-\d+)\s+/);

                // If Google comes back with a range when we only input a single street number
                // then just take what the user intended.
                if (addressNumberMatch && returnedAddressNumberMatch) {
                  const addressNumber = addressNumberMatch[1];
                  const returnedAddressNumber = returnedAddressNumberMatch[1];

                  if (addressNumber && returnedAddressNumber) {
                    componentValues[key] = componentValues[key].replace(returnedAddressNumber, addressNumber);
                  }
                }

                // If the value from the input has a leading street number but the value coming back from
                // Google does not (i.e. input 505 Balsam Terrace Way, but get back Balsam Terrace Way),
                // then we want to make sure that the street number isn't lost when the autocomplete is selected.
                if (addressNumberMatch && !componentValues[key].match(/(^\d+)\s+/)) {
                  componentValues[key] = addressNumberMatch[0] + componentValues[key]; // valueFromGoogle;

                  // For some reason, it was keeping the entire autocompleted result (i.e. address,
                  // city, state, zip, etc) in the address field in addition to parsing them to the
                  // other fields. I'm not sure why this is happening, but it may be something with
                  // the Google Maps API. I'm forcing this generic input to be correct here.
                  this.input.nativeElement.value = componentValues[key];
                }
              }


              this.validationForm.controls[key].setValue(componentValues[key]);
            });
          });
        });

      }, (err) => {
        console.error(err);
        console.error('Your Google MAPS Javascript API key is wrong or the api is not enabled.');
      });
    }
  }

  _handleFocus(event: FocusEvent) {
    this._focused = true;
    this._focusEmitter.emit(event);
  }

  _handleBlur(event: FocusEvent) {
    this._focused = false;
    this._onTouchedCallback();
    this._blurEmitter.emit(event);
  }

  _handleChange(event: Event) {
    this.value = (<HTMLInputElement>event.target).value;
    this._onTouchedCallback();
  }

  _handleInput(event: Event) {
    const el = this.input.nativeElement;

    maskedNumberInputShiftWorkaround(el, this._getAllowedPatternForMaskingType());

    let ret: RegExp;
    switch (this.maskingType) {
      case 'phone':
        el.value = (<HTMLInputElement>event.target).value.replace(/[^0-9\(\)\s\-]/g, '');
        break;
      case 'zipCode':
        el.value = (<HTMLInputElement>event.target).value.replace(/[^0-9\-]/g, '');
        break;
      case 'digitsOnly':
        el.value = (<HTMLInputElement>event.target).value.replace(/\D/g, '');
        break;
      case 'phoneExt':
        el.value = (<HTMLInputElement>event.target).value.replace(/[^x0-9]/g, '');
        break;
      case 'currency':
        if (this.step && this.step === 1) {
          ret = /\d/g;
        } else {
          ret = /[\d.]/g;
        }

        let approved = (el.value.match(ret) || ['']).join('');
        if (approved !== el.value.replace(/\$|,/g, '')) {
          el.value = this._formatCurrencyString(approved);
        }
        break;
    }

    const maxLength = this.getMaxLength();
    if (maxLength > 0 && el.value.length > maxLength) {
      el.value = el.value.substr(0, maxLength);
    }
  }

  _handleKeyPress(event: KeyboardEvent) {
    let exceptionsToTheRule = ['Enter', 'Backspace', 'Tab'];
    if (exceptionsToTheRule.indexOf(event.code) >= 0) {
      return;
    }

    if (this.type === 'number' && event.key.toLowerCase() === 'e') {
      event.preventDefault();
    }

    const pattern = this._getAllowedPatternForMaskingType();
    if (!!pattern) {
      let inputChar = String.fromCharCode(event.charCode);
      if (!pattern.test(inputChar)) {
        event.preventDefault();
      }
    }
  }

  hasErrors() {
    return this.validationForm &&
      this.validationForm.controls[this.formControlName].errors &&
      (this.validationForm.controls[this.formControlName].touched ||
        !this.shouldCheckIsDirty) &&
      !this._focused;
  }

  writeValue(value: any) {
    this._value = value;
  }

  registerOnChange(fn: any) {
    this._onChangeCallback = fn;
  }

  registerOnTouched(fn: any) {
    this._onTouchedCallback = fn;
  }

  coerceBooleanProperty(value: any): boolean {
    return value != null && `${value}` !== 'false';
  }

  /**
   * Convert the value passed in to a value that is expected from the type of the md-input.
   * This is normally performed by the *_VALUE_ACCESSOR in forms, but since the type is bound
   * on our internal input it won't work locally.
   * @private
   */
  private _convertValueForInputType(v: any): any {
    let returnValue = v;
    switch (this.type) {
      case 'number':
        returnValue = this.utils.convertNumberValue(v);

        if (this.step === 1 && this.type === 'number') {
          returnValue = Math.trunc(returnValue);
        }
        break;
      default:
        break;
    }

    return returnValue;
  }

  private _getAllowedPatternForMaskingType() {
    let ret = null;
    const digitsOnly = /\d/g;
    const digitsAndLetterXOnly = /[x0-9]/g;

    switch (this.maskingType) {
      case 'ssn':
      // TODO: what about extensions?
      case 'phone':
      case 'zipCode':
      case 'digitsOnly':
        ret = digitsOnly;
        break;
      case 'phoneExt':
        ret = digitsAndLetterXOnly;
        break;
      case 'currency':
        if (this.step && this.step === 1) {
          ret = digitsOnly;
        } else {
          ret = /[\d.]/g;
        }
        break;
    }

    return ret;
  }
}
